import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  ElementRef,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { UnitSystem } from "../../../../shared/enums/unit-system.enum";
import { PreSimulationService } from "../../../../shared/components/pre-simulation/service/pre-simulation.service";
import { DataService } from "../../../../services/Data.service";
import {
  HeaderActionsService,
  HeaderActionType,
} from "../../../../services/header-actions.service";
import { PdfService } from "../../../../services/pdf.service";
import { NAVIGATIONS } from "../../../../shared/consts/navigations";
import { SaveSimulationForm } from "../../../../shared/components/header-modals/save-simulation-modal/save-simulation-modal.component";
import { AdvancedpipeService } from "../../services/advancedpipe.service";
import { SlottedModalComponent } from "../../../slotted-perforated-pipe/shared/slotted-modal/slotted-modal.component";
import {
  ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS,
  ADVANCEDPIPE_RESULT_DECIMAL_PLACES,
  ADVANCEDPIPE_SPECIFIC_RESULT_DECIMALS,
  ADVANCEDPIPE_HOT_ROTARY_OPTIONS,
  getAdvancedPipeMaxMinValidator,
  getAdvancedPipeDecimalPattern,
} from "../../advancedpipe-validators.constants";
import { getAdvancedPipeFormDefaults } from "../../advancedpipe-defaults";
import {
  UnitConversionService,
  UNITS,
} from "../../../../shared/services/unit-conversion.service";
import Swal from "sweetalert2";
import { SaveSimulationService } from "src/app/shared/components/header-modals/save-simulation-modal/save-simulation.service";

@Component({
  selector: "app-advancedpipe",
  templateUrl: "./advancedpipe.component.html",
  styleUrls: ["./advancedpipe.component.css"],
})
export class AdvancedpipeComponent implements OnInit, OnDestroy {
  title = "Advanced Pipe Ratings";
  public readonly UnitSystem = UnitSystem;
  readonly NAVIGATIONS = NAVIGATIONS;
  unitSystem: UnitSystem = UnitSystem.USC;
  isPreSimulationFormValid = false;
  simulationName: string = "";
  simulationRev: string = "";

  private originalFormValue: any;

  // Avoid overriding values during LoadSimulation patching
  private isPatchingFromLoad: boolean = false;
  currentTreeId: number | null = null;
  customerId: number | null = null;
  countryId: number | null = null;

  // Form and subscriptions
  fgAdvancedpipe!: UntypedFormGroup;
  headerSubscription!: Subscription;
  dcpProduct$!: Subscription;
  loadedVisibility: string | null = null;
  loadedNotes: string | null = null;

  // Cache for attribute-value conversions (used in getOrConvertValue)
  rawByAttrAndValue: { [attrName: string]: { [value: string]: any } } = {};
  isProgrammaticPatch = false;

  // Results
  advancedpipeResults: any = null;

  // Range modal properties
  isRangeModalOpen = false;

  // Collapsible second column
  isSecondColumnCollapsed = true;

  isUser: boolean = true;

  // Modal integration
  @ViewChild(SlottedModalComponent)
  slottedModalComponent!: SlottedModalComponent;

  hotRotaryOptions = ADVANCEDPIPE_HOT_ROTARY_OPTIONS;

  /**
   * Cachea y convierte valores por atributo, guardando ambos sentidos.
   * Si el valor ya está cacheado, lo devuelve. Si no, limpia el cache viejo,
   * realiza la conversión y guarda ambos sentidos.
   * @param attrName Nombre del atributo (string)
   * @param currentValue Valor actual (number|string)
   * @param unitSystem Sistema de unidades destino
   * @param getConvertedValueFn Función de conversión: (value, unitSystem) => valueConverted
   * @returns Valor convertido
   */
  getOrConvertValue(
    attrName: string,
    currentValue: any,
    unitSystem: UnitSystem,
    getConvertedValueFn: (
      value: any,
      unitSystem: UnitSystem,
      attrName?: string,
    ) => any,
    skipConversion: boolean = false,
  ): any {
    if (skipConversion) {
      return currentValue;
    }
    if (!this.rawByAttrAndValue) {
      this.rawByAttrAndValue = {};
    }
    if (!this.rawByAttrAndValue[attrName]) {
      this.rawByAttrAndValue[attrName] = {};
    }
    // Detectar si el valor original está en notación científica
    const isScientific =
      typeof currentValue === "string" && /e[-+]?\d+/i.test(currentValue);
    // Si el valor no está cacheado
    if (this.rawByAttrAndValue[attrName][currentValue] == null) {
      // Elimino viejas conversiones
      this.rawByAttrAndValue[attrName] = {};
      // Hago la conversión, pasando también el nombre del atributo
      let valueConverted = getConvertedValueFn(
        currentValue,
        unitSystem,
        attrName,
      );
      // Si el valor original estaba en notación científica y el convertido es numérico, formatear como string notación científica
      if (isScientific && typeof valueConverted === "number") {
        valueConverted = valueConverted.toExponential(2);
      } else if (typeof valueConverted === "number") {
        if (
          ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS[
            attrName as keyof typeof ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS
          ] &&
          ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS[
            attrName as keyof typeof ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS
          ][unitSystem] != null
        ) {
          // Si no es notación científica, aplicar redondeo según el validador
          const decimals =
            ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS[
              attrName as keyof typeof ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS
            ][unitSystem];
          valueConverted = valueConverted.toFixed(decimals);
        }
      }
      // Guardo ambos sentidos
      this.rawByAttrAndValue[attrName][currentValue] = valueConverted;
      this.rawByAttrAndValue[attrName][valueConverted] = currentValue;
    }
    return this.rawByAttrAndValue[attrName][currentValue];
  }

  constructor(
    private preSimulationService: PreSimulationService,
    private saveSimulationService: SaveSimulationService,
    private dataService: DataService,
    private headerActionsService: HeaderActionsService,
    private pdfService: PdfService,
    private advancedpipeService: AdvancedpipeService,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    public unitService: UnitConversionService,
    private elementRef: ElementRef,
  ) {
    this.createForm();
    this.configFormChanges();
  }

  ngOnInit(): void {
    this.dataService.changeModulo("advancedPipe");

    this.preSimulationService.formValidity$.subscribe((isValid: boolean) => {
      setTimeout(() => {
        this.isPreSimulationFormValid = isValid;
      });
    });

    // Subscribe to header actions
    this.headerSubscription =
      this.headerActionsService.actionRequested$.subscribe((action) => {
        switch (action.type) {
          case HeaderActionType.Save:
            this.handleSaveSimulation(action.payload);
            break;
          case HeaderActionType.Load:
            this.handleLoadSimulation(action.payload);
            break;
          case HeaderActionType.Delete:
            // If the deleted simulation is the currently loaded one, reset the form
            if (
              action.payload &&
              this.simulationName &&
              action.payload.path === this.simulationName
            ) {
              this.resetSimulation();
              this.preSimulationService.resetValues();
            }
            break;
          case HeaderActionType.ValidatePrintPdf:
            if (this.canPrintReport()) {
              this.headerActionsService.requestAction(
                HeaderActionType.OpenPrintPdfModal,
              );
            }
            break;
          case HeaderActionType.ValidatePrintCsv:
            if (this.canPrintReport()) {
              this.headerActionsService.requestAction(
                HeaderActionType.PrintCsv,
              );
            }
            break;
          case HeaderActionType.PrintPdf:
            this.printPdfReport(action.payload);
            break;
          case HeaderActionType.PrintCsv:
            this.printCsvReport();
            break;
          case HeaderActionType.New:
            this.confirmReset();
            break;
        }
      });

    // Subscribe to DCP product selection from modal
    this.dcpProduct$ = this.advancedpipeService.dcpProduct$.subscribe(
      (dcpProduct) => {
        if (!this.fgAdvancedpipe) return;

        // Don't override values during LoadSimulation patching
        if (this.isPatchingFromLoad) return;

        // Set values directly like in geothermal
        this.fgAdvancedpipe
          .get("outerDiameter")
          ?.setValue(
            dcpProduct.outsideDiameter != null
              ? Number(dcpProduct.outsideDiameter).toFixed(
                  ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS["outerDiameter"][
                    this.unitSystem
                  ],
                )
              : null,
          );

        this.fgAdvancedpipe
          .get("wallThickness")
          ?.setValue(
            dcpProduct.wallThickness != null
              ? Number(dcpProduct.wallThickness).toFixed(
                  ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS["wallThickness"][
                    this.unitSystem
                  ],
                )
              : null,
          );

        this.fgAdvancedpipe.get("grade")?.setValue(dcpProduct.grade);

        this.fgAdvancedpipe
          .get("minWallThickness")
          ?.setValue(
            dcpProduct.minWallThickness != null
              ? ((1 + dcpProduct.minWallThickness) * 100).toFixed(
                  ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS["minWallThickness"][
                    this.unitSystem
                  ],
                )
              : null,
          );

        this.advancedpipeService.setCloseDcpModal(true);

        // Show success message after modal closes
        setTimeout(() => {
          Swal.fire({
            icon: "success",
            title: "Product Selected",
            text: "Pipe specifications have been updated with the selected product.",
            timer: 2000,
            showConfirmButton: false,
          });
        }, 300);
      },
    );

    this.configFormChanges();

    this.setRol();
  }

  ngOnDestroy(): void {
    if (this.headerSubscription) {
      this.headerSubscription.unsubscribe();
    }
    if (this.dcpProduct$) {
      this.dcpProduct$.unsubscribe();
    }
  }

  private createForm(): void {
    const defaults = getAdvancedPipeFormDefaults(this.unitSystem);
    this.fgAdvancedpipe = this.fb.group({
      // PIPE section
      outerDiameter: [
        defaults.outerDiameter,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "OUTSIDE_DIAMETER_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "OUTSIDE_DIAMETER_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("outerDiameter", this.unitSystem),
          ),
        ],
      ],
      wallThickness: [
        defaults.wallThickness,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "WALL_THICKNESS_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "WALL_THICKNESS_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("wallThickness", this.unitSystem),
          ),
        ],
      ],
      minWallThickness: [
        defaults.minWallThickness,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "PIPE_WT_REDUCTION_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "PIPE_WT_REDUCTION_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("minWallThickness", this.unitSystem),
          ),
        ],
      ],
      grade: [
        defaults.grade,
        [
          Validators.required,
          Validators.pattern(
            getAdvancedPipeDecimalPattern("grade", this.unitSystem),
          ),
        ],
      ],

      // MATERIAL section
      maxFlawSize: [
        defaults.maxFlawSize,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "MAX_FLAW_SIZE_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "MAX_FLAW_SIZE_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("maxFlawSize", this.unitSystem),
          ),
        ],
      ],
      materialToughness: [
        defaults.materialToughness,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "MATERIAL_TOUGHNESS_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "MATERIAL_TOUGHNESS_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("materialToughness", this.unitSystem),
          ),
        ],
      ],
      ovality: [
        defaults.ovality,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator("OVALITY_MIN", this.unitSystem),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator("OVALITY_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("ovality", this.unitSystem),
          ),
        ],
      ],
      eccentricity: [
        defaults.eccentricity,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator("ECCENTRICITY_MIN", this.unitSystem),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator("ECCENTRICITY_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("eccentricity", this.unitSystem),
          ),
        ],
      ],
      poisson: [
        defaults.poisson,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator("POISSON_MIN", this.unitSystem),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator("POISSON_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("poisson", this.unitSystem),
          ),
        ],
      ],
      residualStress: [
        defaults.residualStress,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "RESIDUAL_STRESS_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "RESIDUAL_STRESS_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("residualStress", this.unitSystem),
          ),
        ],
      ],
      smys: [
        defaults.smys,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator("SMYS_MIN", this.unitSystem),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator("SMYS_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("smys", this.unitSystem),
          ),
        ],
      ],
      uts: [
        defaults.uts,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator("UTS_MIN", this.unitSystem),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator("UTS_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("uts", this.unitSystem),
          ),
        ],
      ],
      youngModulus: [
        defaults.youngModulus,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "YOUNG_MODULUS_MIN",
              this.unitSystem,
            ),
          ),
          Validators.max(
            getAdvancedPipeMaxMinValidator(
              "YOUNG_MODULUS_MAX",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("youngModulus", this.unitSystem),
          ),
        ],
      ],
      hotRotaryStraightening: [
        defaults.hotRotaryStraightening,
        [Validators.required],
      ],

      // LOADS section
      realAxialLoad: [
        defaults.realAxialLoad,
        [
          Validators.required,
          Validators.pattern(
            getAdvancedPipeDecimalPattern("realAxialLoad", this.unitSystem),
          ),
        ],
      ],
      internalPressure: [
        defaults.internalPressure,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "INTERNAL_PRESSURE_MIN",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("internalPressure", this.unitSystem),
          ),
        ],
      ],
      externalPressure: [
        defaults.externalPressure,
        [
          Validators.required,
          Validators.min(
            getAdvancedPipeMaxMinValidator(
              "EXTERNAL_PRESSURE_MIN",
              this.unitSystem,
            ),
          ),
          Validators.pattern(
            getAdvancedPipeDecimalPattern("externalPressure", this.unitSystem),
          ),
        ],
      ],
    });

    this.originalFormValue =  this.fgAdvancedpipe.getRawValue();
  }

  private configFormChanges(): void {
    if (!this.fgAdvancedpipe) return;

    // Add real-time validation for all form controls
    const formControls = [
      "outerDiameter",
      "wallThickness",
      "minWallThickness",
      "grade",
      "maxFlawSize",
      "materialToughness",
      "ovality",
      "eccentricity",
      "residualStress",
      "hotRotaryStraightening",
      "realAxialLoad",
      "internalPressure",
      "externalPressure",
    ];

    formControls.forEach((controlName) => {
      const control = this.fgAdvancedpipe.get(controlName);
      if (control) {
        control.valueChanges.subscribe(() => {
          // Mark as touched to show validation errors immediately
          if (!control.touched) {
            control.markAsTouched();
          }
          // Remove updateValueAndValidity to prevent recursion
          // control.updateValueAndValidity({ onlySelf: true });
        });
      }
    });

    // Clear results when any input changes
    this.fgAdvancedpipe.valueChanges.subscribe(() => {
      this.advancedpipeResults = null;
      this.convertedResults = null;
    });
  }

  onUnitSystemChange(unit: UnitSystem) {
    const prevUnit = this.unitSystem;
    // Update unit system first before validator updates
    this.unitSystem = unit;
    this.advancedpipeService.setUnitSystem(unit);

    if (this.fgAdvancedpipe) {
      // Get new defaults for the target unit system
      const newDefaults = getAdvancedPipeFormDefaults(unit);

      const controls = this.getConvertibleControls();
      controls.forEach((key) => {
        const ctrl = this.fgAdvancedpipe.get(key);
        if (!ctrl) return;
        const val = ctrl.value;

        // Si el valor está vacío o es null, usar el nuevo default
        if (val === null || val === undefined || val === "") {
          const defaultValue = (newDefaults as any)[key];
          if (defaultValue !== null && defaultValue !== undefined) {
            setTimeout(() => {
              ctrl.setValue(defaultValue, { emitEvent: false });
              ctrl.updateValueAndValidity();
            }, 0);
          }
          return;
        }

        // Detectar si el valor original está en notación científica
        const isScientific = typeof val === "string" && /e[-+]?\d+/i.test(val);
        let converted = this.getOrConvertValue(
          key,
          val,
          unit,
          (value, unitSystem) => {
            const fromUnit = this.getUnitForControl(key, prevUnit);
            const toUnit = this.getUnitForControl(key, unitSystem);
            return this.convertValue(value, fromUnit, toUnit);
          },
        );
        // Si el valor original estaba en notación científica, mostrar el convertido también así
        if (isScientific && typeof converted === "number") {
          converted = converted.toExponential(2);
        }

        setTimeout(() => {
          ctrl.setValue(converted, { emitEvent: false });
          ctrl.updateValueAndValidity();
        }, 0);
      });

      // Update validators for all form controls with new unit system
      this.updateFormValidators(unit);

      this.cdr.detectChanges();
    }

    // Update DCP modal filters for new unit system
    const filterValues = this.buildDcpFilterValues();
    if (this.slottedModalComponent) {
      this.slottedModalComponent.preloadFilters(
        filterValues,
        this.unitSystem,
        true,
      );
    }
  }

  showRangeModal() {
    this.isRangeModalOpen = true;
  }

  closeRangeModal() {
    this.isRangeModalOpen = false;
  }

  // Convertir valor de resultado de USC a SI si es necesario
  convertResultValue(value: any, field: string): string {
    if (
      !value ||
      value === "-" ||
      value === "NaN" ||
      value === null ||
      value === undefined
    ) {
      return null;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return value; // Retornar tal como está si no es numérico
    }

    // Obtener decimales apropiados según el campo específico del resultado o el tipo genérico
    const decimals = this.getResultDecimals(field);

    if (this.unitSystem === UnitSystem.USC) {
      return numValue.toFixed(decimals); // Mostrar en USC con decimales apropiados
    }

    // Convertir de USC a SI
    let convertedValue = numValue;
    switch (field) {
      case "pipeID":
      case "length": // Para campos de longitud
        convertedValue = this.unitService.convert(numValue, "in", "mm");
        break;
      case "pressure":
        convertedValue = this.unitService.convert(
          numValue,
          UNITS.PRESSURE_KSI,
          UNITS.PRESSURE_MPA,
        );
        break;
      case "stress":
        // Residual Stress es un ratio adimensional (Residual Stress / SMYS), no necesita conversión
        convertedValue = numValue;
        break;
      case "area":
        // in² to mm²: 1 in² = 645.16 mm²
        convertedValue = numValue * 645.16;
        break;
      case "moment":
        // in⁴ to mm⁴: 1 in⁴ = 416231.4 mm⁴
        convertedValue = numValue * 416231.4;
        break;
      case "weight":
        convertedValue = this.unitService.convert(
          numValue,
          UNITS.WEIGHT_LB,
          UNITS.WEIGHT_KG,
        );
        break;
      case "force":
        convertedValue = this.unitService.convert(
          numValue,
          UNITS.FORCE_KIPS,
          UNITS.FORCE_KN,
        );
        break;
      case "dimensionless":
      default:
        convertedValue = numValue; // Sin conversión para dimensionless, etc.
    }

    return convertedValue.toFixed(decimals);
  }

  // Nuevo método específico para convertir valores con nombre de campo específico
  convertSpecificResultValue(
    value: any,
    field: string,
    resultFieldName: string,
  ): string {
    if (
      !value ||
      value === "-" ||
      value === "NaN" ||
      value === null ||
      value === undefined
    ) {
      return "-";
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return value;
    }

    // Obtener decimales específicos para este campo de resultado
    const decimals = this.getSpecificResultDecimals(resultFieldName);

    if (this.unitSystem === UnitSystem.USC) {
      return numValue.toFixed(decimals);
    }

    // Convertir de USC a SI según el tipo
    let convertedValue = numValue;
    switch (field) {
      case "pipeID":
      case "length":
        convertedValue = this.unitService.convert(numValue, "in", "mm");
        break;
      case "pressure":
        convertedValue = this.unitService.convert(
          numValue,
          UNITS.PRESSURE_KSI,
          UNITS.PRESSURE_MPA,
        );
        break;
      case "stress":
        convertedValue = numValue;
        break;
      case "area":
        convertedValue = numValue * 645.16;
        break;
      case "moment":
        convertedValue = numValue * 416231.4;
        break;
      case "weight":
        convertedValue = this.unitService.convert(
          numValue,
          UNITS.WEIGHT_LB,
          UNITS.WEIGHT_KG,
        );
        break;
      case "force":
        convertedValue = this.unitService.convert(
          numValue,
          UNITS.FORCE_KIPS,
          UNITS.FORCE_KN,
        );
        break;
      case "dimensionless":
      default:
        convertedValue = numValue;
    }

    return convertedValue.toFixed(decimals);
  }

  // Obtener decimales específicos para un campo de resultado particular
  getSpecificResultDecimals(resultFieldName: string): number {
    if ((ADVANCEDPIPE_SPECIFIC_RESULT_DECIMALS as any)[resultFieldName]) {
      return (
        (ADVANCEDPIPE_SPECIFIC_RESULT_DECIMALS as any)[resultFieldName][
          this.unitSystem
        ] ?? 2
      );
    }
    return 2; // Valor por defecto
  }

  // Obtener decimales específicos para cada tipo de resultado según el sistema de unidades
  getResultDecimals(field: string): number {
    // Usar la configuración por tipo de campo
    if ((ADVANCEDPIPE_RESULT_DECIMAL_PLACES as any)[field]) {
      return (
        (ADVANCEDPIPE_RESULT_DECIMAL_PLACES as any)[field][this.unitSystem] ?? 2
      );
    }

    // Valor por defecto
    return (
      (ADVANCEDPIPE_RESULT_DECIMAL_PLACES as any)["default"][this.unitSystem] ??
      2
    );
  }

  // Obtener la unidad apropiada según el sistema
  getResultUnit(field: string): string {
    switch (field) {
      case "pipeID":
      case "length":
        return this.unitSystem === UnitSystem.USC
          ? UNITS.LENGTH_IN
          : UNITS.LENGTH_MM;
      case "pressure":
      case "stress":
        return this.unitSystem === UnitSystem.USC
          ? UNITS.PRESSURE_KSI
          : UNITS.PRESSURE_MPA;
      case "weight":
        return this.unitSystem === UnitSystem.USC
          ? UNITS.WEIGHT_LB
          : UNITS.WEIGHT_KG;
      case "force":
        return this.unitSystem === UnitSystem.USC
          ? UNITS.FORCE_KIPS
          : UNITS.FORCE_KN;
      case "dimensionless":
        return "-";
      case "area":
        return this.unitSystem === UnitSystem.USC ? "in²" : "mm²";
      case "moment":
        return this.unitSystem === UnitSystem.USC ? "in⁴" : "mm⁴";
      default:
        return "-";
    }
  }

  toggleSecondColumn() {
    this.isSecondColumnCollapsed = !this.isSecondColumnCollapsed;

    // Si se está expandiendo, hacer scroll hasta el final después de la animación
    if (!this.isSecondColumnCollapsed) {
      setTimeout(() => {
        this.scrollToDetailedCalculations();
      }, 650); // Esperar un poco más que la animación (600ms)
    }
  }

  loadModal() {
    // To avoid problems with the unit system changes (same as geothermal)
    const filterValues = {
      outsideDiameter: null,
      wallThickness: null,
      nominalWeight: null,
      grade: null,
      connection: null,
      minWallThickness: null,
    };

    // Configure filters first
    if (this.slottedModalComponent) {
      this.slottedModalComponent.preloadFilters(
        filterValues,
        this.unitSystem,
        true,
      );
    }

    // Use setTimeout to ensure DOM is ready before opening modal
    setTimeout(() => {
      try {
        ($ as any)("#dcpModal").modal("show");
      } catch (error) {
        // Fallback: try alternative approach
        const modalElement = document.getElementById("dcpModal");
        if (modalElement) {
          modalElement.style.display = "block";
          modalElement.classList.add("show");
          document.body.classList.add("modal-open");
        }
      }
    }, 100);
  }

  // ===== NEW SIMULATION FUNCTIONALITY =====
  private confirmReset(
    afterResetCallback?: () => void,
    customMessage?: string,
  ) {
    if (this.hasFormValues()) {
      Swal.fire({
        title: "Create a new simulation",
        text:
          customMessage ||
          "All current values will be lost. Do you want to proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#009",
        cancelButtonColor: "#aaa",
        confirmButtonText: "Proceed",
      }).then((result) => {
        if (result.isConfirmed) {
          this.resetSimulation();
          this.preSimulationService.resetValues();
          if (afterResetCallback) {
            afterResetCallback();
          }
          Swal.fire("Done", "New simulation ready", "success");
        }
      });
    } else {
      this.resetSimulation();
      this.preSimulationService.resetValues();
      if (afterResetCallback) {
        afterResetCallback();
      }
    }
  }

  private hasFormValues(): boolean {
    if (!this.fgAdvancedpipe) return false;
    const formValues = this.fgAdvancedpipe.value;
    return Object.values(formValues).some(
      (value) => value !== "" && value !== null && value !== undefined,
    );
  }

  private resetSimulation() {
    // Clear simulation context
    this.simulationName = "";
    this.simulationRev = "";
    this.currentTreeId = null;
    this.customerId = null;
    this.countryId = null;

    // Reset visibility and notes
    this.loadedVisibility = null;
    this.loadedNotes = null;
    this.saveSimulationService.setSavedVisibility(null);
    this.saveSimulationService.setSavedNotes(null);

    this.saveSimulationService.setAllSavedValues({});

    // Clear results and reset form with defaults
    this.advancedpipeResults = null;
    this.convertedResults = null;

    if (this.fgAdvancedpipe) {
      // Get defaults for current unit system and apply them
      const defaults = getAdvancedPipeFormDefaults(this.unitSystem);
      Object.keys(this.fgAdvancedpipe.controls).forEach((key) => {
        const ctrl = this.fgAdvancedpipe.get(key);
        if (ctrl) {
          const defaultValue = (defaults as any)[key];
          ctrl.setValue(defaultValue !== undefined ? defaultValue : null, {
            emitEvent: false,
          });
          ctrl.markAsPristine();
          ctrl.markAsUntouched();
          ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
      });
    }

    this.originalFormValue = this.fgAdvancedpipe.getRawValue();
  }

  private setRol() {
    const roles = this.dataService.getCurrentRol();
    if (roles && roles.some((x) => x == "ADMIN")) {
      this.isUser = false;
    } else if (roles && roles.some((x) => x == "SUPER_USER")) {
      this.isUser = false;
    } else if (roles && roles.some((x) => x == "USER")) {
      this.isUser = true;
    }
  }

  // ===== SAVE SIMULATION FUNCTIONALITY =====
  private handleSaveSimulation(saveSimulationForm: SaveSimulationForm) {
    if (!saveSimulationForm.simulation.id) {
      // Nueva simulación
      this.loadedVisibility = null;
      this.loadedNotes = null;
      this.saveSimulationService.setSavedVisibility(null);
      this.saveSimulationService.setSavedNotes(null);
      this.saveSimulation(saveSimulationForm);
      return;
    } else {
      // Guardar/actualizar
      if (this.loadedVisibility !== null) {
        this.saveSimulationService.setSavedVisibility(this.loadedVisibility);
      }
      if (this.loadedNotes !== null) {
        this.saveSimulationService.setSavedNotes(this.loadedNotes);
      }
      this.confirmOverwrite(saveSimulationForm);
    }
  }

  private confirmOverwrite(saveSimulationForm: SaveSimulationForm) {
    Swal.fire({
      title: "Do you want to overwrite the simulation",
      text: saveSimulationForm.path + " ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Proceed",
    }).then((result) => {
      if (result.isConfirmed) {
        this.saveSimulation(saveSimulationForm);
      }
    });
  }

  private saveSimulation(saveSimulationForm: SaveSimulationForm) {
    const advancedpipeRequest =
      this.getAdvancedpipeSaveRequest(saveSimulationForm);

    this.advancedpipeService
      .saveSimulation(advancedpipeRequest, saveSimulationForm)
      .subscribe((response) => {
        if (response.status.success) {
          this.currentTreeId = response.data.treeId;
          this.simulationName = saveSimulationForm.path;
          this.dataService.setSimulationIdName(saveSimulationForm.path);
          this.simulationRev = response.data.rev;
            this.customerId = advancedpipeRequest.customerId;
            this.countryId = advancedpipeRequest.countryId;
          this.loadedVisibility = saveSimulationForm.visibility ?? null;
          this.loadedNotes = saveSimulationForm.notes ?? null;
          this.saveSimulationService.setSavedVisibility(
            saveSimulationForm.visibility ?? null,
          );
          this.saveSimulationService.setSavedNotes(
            saveSimulationForm.notes ?? null,
          );

          // Save simulation context
          this.saveSimulationService.setAllSavedValues({
            customerId: saveSimulationForm.customer?.id ?? null,
            regionId: saveSimulationForm.region?.id ?? null,
            projectId: saveSimulationForm.project?.id ?? null,
            simulationId: saveSimulationForm.simulation?.id ?? null,
            customerName: saveSimulationForm.customer?.description ?? null,
            regionName: saveSimulationForm.region?.description ?? null,
            projectName: saveSimulationForm.project?.description ?? null,
            simulationName: saveSimulationForm.simulation?.description ?? null,
            visibility: saveSimulationForm.visibility ?? null,
            notes: saveSimulationForm.notes ?? null,
          });

          this.originalFormValue = this.fgAdvancedpipe.getRawValue();

          Swal.fire({
            title: "Simulation saved",
            text: "Simulation has been saved successfully",
            icon: "success",
            confirmButtonColor: "#009",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: response.status.message || "Failed to save simulation",
            icon: "error",
            confirmButtonColor: "#009",
          });
        }
      });
  }

  private getAdvancedpipeSaveRequest(
    saveSimulationForm?: SaveSimulationForm,
  ): any {
    const treeId = saveSimulationForm?.simulation.id;
    // IDs from PreSimulation header (customer/country selected in header)
    const customerId = this.preSimulationService.getCurrentCustomerId();
    const countryId = this.preSimulationService.getCurrentCountryId();
    const notes = saveSimulationForm?.notes ?? null;
    const visibility = saveSimulationForm?.visibility ?? null;

    return {
      treeId,
      customerId,
      countryId,
      notes,
      visibility,
      calculationData: {
        unitSystem: this.unitSystem,
        outerDiameter: this.fgAdvancedpipe.value.outerDiameter,
        wallThickness: this.fgAdvancedpipe.value.wallThickness,
        minWallThickness: this.fgAdvancedpipe.value.minWallThickness,
        grade: this.fgAdvancedpipe.value.grade,
        maxFlawSize: this.fgAdvancedpipe.value.maxFlawSize,
        materialToughness: this.fgAdvancedpipe.value.materialToughness,
        ovality: this.fgAdvancedpipe.value.ovality,
        eccentricity: this.fgAdvancedpipe.value.eccentricity,
        poisson: this.fgAdvancedpipe.value.poisson,
        residualStress: this.fgAdvancedpipe.value.residualStress,
        smys: this.fgAdvancedpipe.value.smys,
        uts: this.fgAdvancedpipe.value.uts,
        youngModulus: this.fgAdvancedpipe.value.youngModulus,
        hotRotaryStraightening:
          this.fgAdvancedpipe.value.hotRotaryStraightening,
        realAxialLoad: this.fgAdvancedpipe.value.realAxialLoad,
        internalPressure: this.fgAdvancedpipe.value.internalPressure,
        externalPressure: this.fgAdvancedpipe.value.externalPressure,
      },
      calculationResult: {
        hoopStressOD: this.convertedResults?.hoopStressOD ?? null,
        vmeStressID: this.convertedResults?.vmeStressID ?? null,
        radialStressID: this.convertedResults?.radialStressID ?? null,
        hoopStressID: this.convertedResults?.hoopStressID ?? null,
        burstKleverStuart: this.convertedResults?.burstKleverStuart ?? null,
        burstHill: this.convertedResults?.burstHill ?? null,
        burstCappedEnd: this.convertedResults?.burstCappedEnd ?? null,
        collapseKGT: this.convertedResults?.collapseKGT ?? null,
        collapseModeAPI: this.convertedResults?.collapseModeAPI ?? null,
        collapseAPI: this.convertedResults?.collapseAPI ?? null,
        burstAPI: this.convertedResults?.burstAPI ?? null,
        pBtensionAPI: this.convertedResults?.pBtensionAPI ?? null,
        effectiveTension: this.convertedResults?.effectiveTension ?? null,
        dsuTcritTransitionColl:
          this.convertedResults?.dsuTcritTransitionColl ?? null,
        dsuTcritPlasticColl: this.convertedResults?.dsuTcritPlasticColl ?? null,
        dsuTcritYieldColl: this.convertedResults?.dsuTcritYieldColl ?? null,
        apIcollConstG: this.convertedResults?.apIcollConstG ?? null,
        apIcollConstF: this.convertedResults?.apIcollConstF ?? null,
        apIcollConstC: this.convertedResults?.apIcollConstC ?? null,
        apIcollConstB: this.convertedResults?.apIcollConstB ?? null,
        apIcollConstA: this.convertedResults?.apIcollConstA ?? null,
        adjusedYieldStrength:
          this.convertedResults?.adjusedYieldStrength ?? null,
        axialStress: this.convertedResults?.axialStress ?? null,
        equivalentCollapsePressure:
          this.convertedResults?.equivalentCollapsePressure ?? null,
        deltaP: this.convertedResults?.deltaP ?? null,
        dsuT: this.convertedResults?.dsuT ?? null,
        moi: this.convertedResults?.moi ?? null,
        crossSectionArea: this.convertedResults?.crossSectionArea ?? null,
        pipeID: this.convertedResults?.pipeID ?? null,
        radialStressOD: this.convertedResults?.radialStressOD ?? null,
        vmeStressOD: this.convertedResults?.vmeStressOD ?? null,
      },
    };
  }

  // ===== LOAD SIMULATION FUNCTIONALITY =====
  private handleLoadSimulation(selectedSimulation: any) {
    const treeId = selectedSimulation?.simulation?.id;
    if (treeId == null) {
      this.fireWarningModal("Action required", "Error loading simulation.");
      return;
    }

    this.currentTreeId = treeId;
    const customerName = selectedSimulation?.customer?.name ?? "";
    const regionName = selectedSimulation?.region?.name ?? "";
    const projectName = selectedSimulation?.project?.name ?? "";
    const simulationName =
      selectedSimulation?.simulation?.name ?? selectedSimulation?.path ?? "";
    this.simulationName = [
      customerName,
      regionName,
      projectName,
      simulationName,
    ]
      .filter(Boolean)
      .join("-");

    this.advancedpipeService
      .loadSimulation(selectedSimulation.simulation.id)
      .subscribe({
        next: (response) => {
          if (response.status.success) {
            this.isPatchingFromLoad = true;
            this.updateFormValidators(response.data.calculationData.unitSystem);
            this.currentTreeId = selectedSimulation.simulation.id;
            this.simulationName = selectedSimulation.path;
            this.simulationRev = response.data.rev;
            this.customerId = response.data.customerId;
            this.countryId = response.data.countryId;
            this.loadedVisibility = response.data.visibility ?? null;
            this.loadedNotes = response.data.notes ?? null;
            this.preSimulationService.setLoadedCustomerId(this.customerId);
            this.preSimulationService.setLoadedCountryId(this.countryId);
            this.preSimulationService.setSavedCustomerId(this.customerId);
            this.preSimulationService.setSavedCountryId(this.countryId);
            if (response.data.calculationData) {
              const calculationData = response.data.calculationData;
              const calculationResult = response.data.calculationResult;
              if (calculationData.unitSystem) {
                this.unitSystem = calculationData.unitSystem;
              }
              // Patch all calculationData fields except unitSystem
              const { unitSystem, ...formFields } = calculationData;
              this.fgAdvancedpipe.patchValue(formFields);
              if (calculationResult) {
                this.advancedpipeResults = calculationResult;
                this.processResults(this.advancedpipeResults);
              }
            }

            this.isPatchingFromLoad = false;

            // Save loaded simulation context for future saves
            this.saveSimulationService.setAllSavedValues({
              customerId: selectedSimulation?.customer?.id ?? null,
              regionId: selectedSimulation?.region?.id ?? null,
              projectId: selectedSimulation?.project?.id ?? null,
              simulationId: selectedSimulation?.simulation?.id ?? null,
              customerName:
                selectedSimulation?.customer?.description ??
                selectedSimulation?.customer?.name ??
                null,
              regionName:
                selectedSimulation?.region?.description ??
                selectedSimulation?.region?.name ??
                null,
              projectName:
                selectedSimulation?.project?.description ??
                selectedSimulation?.project?.name ??
                null,
              simulationName:
                selectedSimulation?.simulation?.description ??
                selectedSimulation?.simulation?.name ??
                null,
              visibility: null,
              notes: null,
            });

            const ownerUserId =
              selectedSimulation?.userId ??
              selectedSimulation?.user?.id ??
              null;
            const currentUserId = this.dataService.getCurrentUserId();

            if (ownerUserId && currentUserId && ownerUserId === currentUserId) {
              if (typeof response.data.visibility === "string") {
                this.saveSimulationService.setSavedVisibility(
                  response.data.visibility,
                );
              }
              if (typeof response.data.notes === "string") {
                this.saveSimulationService.setSavedNotes(response.data.notes);
              }
            }

            this.dataService.setSimulationIdName(this.simulationName);

            this.originalFormValue = this.fgAdvancedpipe.getRawValue();

            Swal.fire({
              title: "Simulation loaded",
              icon: "success",
              confirmButtonColor: "#009",
              timer: 2000,
              timerProgressBar: true,
            });
          }
        },
        error: (error) => {
          this.isPatchingFromLoad = false;
          Swal.fire({
            title: "Error",
            text: "Failed to load simulation",
            icon: "error",
            confirmButtonColor: "#009",
          });
        },
      });
  }

  // ===== PRINT FUNCTIONALITY =====
  printPdfReport(printingModalData: any) {
    if (this.advancedpipeResults) {
      const printRequest = this.getAdvancedpipePrintRequest(printingModalData);
      this.pdfService.getPdfAdvancedPipe(printRequest);
    }
  }

  printCsvReport() {
    if (this.advancedpipeResults) {
      const advancedpipeRequest = this.getAdvancedpipeSaveRequest();
      advancedpipeRequest.UserName =
        this.dataService.getCurrentNameSurname() ?? null;
      advancedpipeRequest.Notes = this.loadedNotes ?? null;
      advancedpipeRequest.Visibility = this.loadedVisibility ?? null;
      advancedpipeRequest.Rev = this.simulationRev ?? null;

      this.pdfService.getCsvAdvancedPipe(advancedpipeRequest);
    }
  }

  canPrintReport(): boolean {
    if (!this.preSimulationService.getFormLoadedValidity()) {
      this.fireWarningModal(
        "Action required",
        "Please select customer and country to download the report",
      );
      return false;
    }

    if (!this.isFormValidAndComplete()) {
      this.fireWarningModal(
        "Action required",
        "Please verify all values to print report",
      );
      return false;
    }

    if (!this.isSimulationSaved()) {
      this.fireWarningModal(
        "Action required",
        "Please save simulation to print report",
      );
      return false;
    }

    return true;
  }

  private isFormValidAndComplete(): boolean {
    return (
      this.fgAdvancedpipe?.valid &&
      this.advancedpipeResults != null &&
      this.convertedResults != null
    );
  }

  private isSimulationSaved(): boolean {
    return (
      !this.isFormModified &&
      this.validateIfPreSimulationSaved(this.customerId, this.countryId)
    );
  }

  private normalizeValuesToString(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
      return obj.map((v) => this.normalizeValuesToString(v));
    }
    if (typeof obj === 'object') {
      const result: any = {};
      Object.keys(obj).forEach((k) => {
        result[k] = this.normalizeValuesToString(obj[k]);
      });
      return result;
    }

    return obj !== null && obj !== undefined ? String(obj) : obj;
  }

  private get isFormModified(): boolean {
    const current = this.normalizeValuesToString(this.fgAdvancedpipe.getRawValue());
    const original = this.normalizeValuesToString(this.originalFormValue);
    return JSON.stringify(current) !== JSON.stringify(original);
  }

  private validateIfPreSimulationSaved(
    customerId: number | null,
    countryId: number | null,
  ): boolean {
    let isSaved = true;
    if (
      this.preSimulationService.getCurrentCountryId() == null ||
      this.preSimulationService.getCurrentCustomerId() == null ||
      this.preSimulationService.getCurrentCountryId() != countryId ||
      this.preSimulationService.getCurrentCustomerId() != customerId
    ) {
      isSaved = false;
    }

    return isSaved;
  }

  private normalizeCreatedBy(val: any): string {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return val.filter(Boolean).join(" ").trim();
    try {
      return String(val);
    } catch {
      return "";
    }
  }

  private getAdvancedpipePrintRequest(printingModalData: any): any {
    return {
      header: {
        createdBy: this.normalizeCreatedBy(
          this.dataService.getCurrentNameSurname(),
        ),
        modelVer: printingModalData?.modelVer ?? this.simulationRev,
        sdCaseNumber: printingModalData?.SDCaseNumber ?? "",
        requestedBy: printingModalData?.requestedBy ?? "",
        reviewedBy: printingModalData?.reviewedBy ?? "",
        authorizedBy: printingModalData?.authorizedBy ?? "",
        preMachiningManufacturer:
          printingModalData?.preMachiningManufacturer ?? "",
      },
      calculationData: {
        unitSystem: this.unitSystem,
        outerDiameter: this.fgAdvancedpipe.value.outerDiameter,
        wallThickness: this.fgAdvancedpipe.value.wallThickness,
        minWallThickness: this.fgAdvancedpipe.value.minWallThickness,
        grade: this.fgAdvancedpipe.value.grade,
        maxFlawSize: this.fgAdvancedpipe.value.maxFlawSize,
        materialToughness: this.fgAdvancedpipe.value.materialToughness,
        ovality: this.fgAdvancedpipe.value.ovality,
        eccentricity: this.fgAdvancedpipe.value.eccentricity,
        poisson: this.fgAdvancedpipe.value.poisson,
        residualStress: this.fgAdvancedpipe.value.residualStress,
        smys: this.fgAdvancedpipe.value.smys,
        uts: this.fgAdvancedpipe.value.uts,
        youngModulus: this.fgAdvancedpipe.value.youngModulus,
        hotRotaryStraightening:
          this.fgAdvancedpipe.value.hotRotaryStraightening,
        realAxialLoad: this.fgAdvancedpipe.value.realAxialLoad,
        internalPressure: this.fgAdvancedpipe.value.internalPressure,
        externalPressure: this.fgAdvancedpipe.value.externalPressure,
      },
      calculationResult: {
        hoopStressOD: this.convertedResults?.hoopStressOD ?? null,
        vmeStressID: this.convertedResults?.vmeStressID ?? null,
        radialStressID: this.convertedResults?.radialStressID ?? null,
        hoopStressID: this.convertedResults?.hoopStressID ?? null,
        burstKleverStuart: this.convertedResults?.burstKleverStuart ?? null,
        burstHill: this.convertedResults?.burstHill ?? null,
        burstCappedEnd: this.convertedResults?.burstCappedEnd ?? null,
        collapseKGT: this.convertedResults?.collapseKGT ?? null,
        collapseModeAPI: this.convertedResults?.collapseModeAPI ?? null,
        collapseAPI: this.convertedResults?.collapseAPI ?? null,
        burstAPI: this.convertedResults?.burstAPI ?? null,
        pBtensionAPI: this.convertedResults?.pBtensionAPI ?? null,
        effectiveTension: this.convertedResults?.effectiveTension ?? null,
        dsuTcritTransitionColl:
          this.convertedResults?.dsuTcritTransitionColl ?? null,
        dsuTcritPlasticColl: this.convertedResults?.dsuTcritPlasticColl ?? null,
        dsuTcritYieldColl: this.convertedResults?.dsuTcritYieldColl ?? null,
        apIcollConstG: this.convertedResults?.apIcollConstG ?? null,
        apIcollConstF: this.convertedResults?.apIcollConstF ?? null,
        apIcollConstC: this.convertedResults?.apIcollConstC ?? null,
        apIcollConstB: this.convertedResults?.apIcollConstB ?? null,
        apIcollConstA: this.convertedResults?.apIcollConstA ?? null,
        adjusedYieldStrength:
          this.convertedResults?.adjusedYieldStrength ?? null,
        axialStress: this.convertedResults?.axialStress ?? null,
        equivalentCollapsePressure:
          this.convertedResults?.equivalentCollapsePressure ?? null,
        deltaP: this.convertedResults?.deltaP ?? null,
        dsuT: this.convertedResults?.dsuT ?? null,
        moi: this.convertedResults?.moi ?? null,
        crossSectionArea: this.convertedResults?.crossSectionArea ?? null,
        pipeID: this.convertedResults?.pipeID ?? null,
        radialStressOD: this.convertedResults?.radialStressOD ?? null,
        vmeStressOD: this.convertedResults?.vmeStressOD ?? null,
      },
    };
  }

  private fireWarningModal(title: string, text: string) {
    Swal.fire({
      title: title,
      text: text,
      icon: "warning",
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
    });
  }

  // ===== DCP MODAL INTEGRATION METHODS =====
  private buildDcpFilterValues(): any {
    // Build filter values based on current form data
    const formValues = this.fgAdvancedpipe.value;

    return {
      outsideDiameter: formValues.fgPipe?.outerDiameter || null,
      wallThickness: formValues.fgPipe?.wallThickness || null,
      grade: formValues.grade || null, // Get actual grade value from form
      connection: null, // Add if form has connection selection
      minWallThickness: null, // Add if relevant
    };
  }

  // ===== UNIT CONVERSION METHODS =====
  private getConvertibleControls(): string[] {
    return [
      // PIPE section
      "outerDiameter",
      "wallThickness",
      "minWallThickness",
      // MATERIAL section
      "maxFlawSize",
      "materialToughness",
      "ovality",
      "eccentricity",
      "residualStress",
      "smys",
      "uts",
      "youngModulus",
      // LOADS section
      "realAxialLoad",
      "internalPressure",
      "externalPressure",
    ];
  }

  private getUnitForControl(
    control: string,
    system: UnitSystem,
  ): string | null {
    const USC = UnitSystem.USC;
    // Map to UnitConversionService unit symbol strings
    const from = (s: UnitSystem) => {
      switch (control) {
        case "outerDiameter":
        case "wallThickness":
        case "maxFlawSize":
          return s === USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;
        case "ovality":
        case "eccentricity":
          return "%";
        case "smys":
        case "uts":
        case "youngModulus":
        case "internalPressure":
        case "externalPressure":
          return s === USC ? UNITS.PRESSURE_KSI : UNITS.PRESSURE_MPA;
        case "minWallThickness":
          return "%"; // No conversion needed
        case "realAxialLoad":
          return s === USC ? UNITS.FORCE_KIPS : UNITS.FORCE_KN; // kips to kN
        case "residualStress": // No unit - dimensionless ratio
        case "materialToughness":
        case "poisson": // No unit - dimensionless ratio
          return null;
        default:
          return null;
      }
    };
    return from(system);
  }

  private convertValue(
    value: any,
    fromUnit: string | null,
    toUnit: string | null,
  ): number | null {
    if (value == null || value === "") return null;
    const num = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(num)) return null;
    if (!fromUnit || !toUnit || fromUnit === toUnit) return num;

    try {
      const result = this.unitService.convert(num, fromUnit, toUnit);
      return result;
    } catch (error) {
      return num;
    }
  }

  private updateFormValidators(unitSystem: UnitSystem): void {
    if (!this.fgAdvancedpipe) return;

    const formControlValidators: { [key: string]: string[] } = {
      outerDiameter: ["OUTSIDE_DIAMETER_MIN", "OUTSIDE_DIAMETER_MAX"],
      wallThickness: ["WALL_THICKNESS_MIN", "WALL_THICKNESS_MAX"],
      minWallThickness: ["PIPE_WT_REDUCTION_MIN", "PIPE_WT_REDUCTION_MAX"],
      maxFlawSize: ["MAX_FLAW_SIZE_MIN", "MAX_FLAW_SIZE_MAX"],
      materialToughness: ["MATERIAL_TOUGHNESS_MIN", "MATERIAL_TOUGHNESS_MAX"],
      ovality: ["OVALITY_MIN", "OVALITY_MAX"],
      eccentricity: ["ECCENTRICITY_MIN", "ECCENTRICITY_MAX"],
      poisson: ["POISSON_MIN", "POISSON_MAX"],
      residualStress: ["RESIDUAL_STRESS_MIN", "RESIDUAL_STRESS_MAX"],
      smys: ["SMYS_MIN", "SMYS_MAX"],
      uts: ["UTS_MIN", "UTS_MAX"],
      youngModulus: ["YOUNG_MODULUS_MIN", "YOUNG_MODULUS_MAX"],
    };

    // Fields with only minimum validation
    const minOnlyValidators = {
      internalPressure: "INTERNAL_PRESSURE_MIN",
      externalPressure: "EXTERNAL_PRESSURE_MIN",
    };

    // Fields without min/max that need pattern validation only
    const patternOnlyFields = ["realAxialLoad"];

    Object.keys(formControlValidators).forEach((controlName) => {
      const control = this.fgAdvancedpipe.get(controlName);
      if (!control) return;

      const [minKey, maxKey] = formControlValidators[controlName];
      const min = getAdvancedPipeMaxMinValidator(minKey, unitSystem);
      const max = getAdvancedPipeMaxMinValidator(maxKey, unitSystem);

      // Update validators with pattern included
      control.setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(
          getAdvancedPipeDecimalPattern(controlName, unitSystem),
        ),
      ]);

      control.updateValueAndValidity({ onlySelf: true });
    });

    // Handle fields with only minimum validation
    Object.keys(minOnlyValidators).forEach((controlName) => {
      const control = this.fgAdvancedpipe.get(controlName);
      if (!control) return;

      const minKey = minOnlyValidators[controlName];
      const min = getAdvancedPipeMaxMinValidator(minKey, unitSystem);

      // Update validators with only minimum
      control.setValidators([
        Validators.required,
        Validators.min(min),
        Validators.pattern(
          getAdvancedPipeDecimalPattern(controlName, unitSystem),
        ),
      ]);

      control.updateValueAndValidity({ onlySelf: true });
    });

    // Special cases for fields that don't have min/max validators
    const gradeControl = this.fgAdvancedpipe.get("grade");
    if (gradeControl) {
      gradeControl.setValidators([
        Validators.required,
        Validators.pattern(getAdvancedPipeDecimalPattern("grade", unitSystem)),
      ]);
      gradeControl.updateValueAndValidity({ onlySelf: true });
    }

    // Handle fields that only need pattern validation (no min/max)
    patternOnlyFields.forEach((controlName) => {
      const control = this.fgAdvancedpipe.get(controlName);
      if (!control) return;

      control.setValidators([
        Validators.required,
        Validators.pattern(
          getAdvancedPipeDecimalPattern(controlName, unitSystem),
        ),
      ]);
      control.updateValueAndValidity({ onlySelf: true });
    });

    const hotRotaryControl = this.fgAdvancedpipe.get("hotRotaryStraightening");
    if (hotRotaryControl) {
      hotRotaryControl.setValidators([Validators.required]);
      hotRotaryControl.updateValueAndValidity({ onlySelf: true });
    }
  }

  onSubmit(): void {
    if (!this.fgAdvancedpipe.valid) {
      // Reveal and focus first invalid control for quick fixing
      this.fgAdvancedpipe.markAllAsTouched();
      Object.keys(this.fgAdvancedpipe.controls).forEach((key) =>
        this.fgAdvancedpipe
          .get(key)
          ?.updateValueAndValidity({ onlySelf: true }),
      );
      return;
    }

    if (this.fgAdvancedpipe.valid) {
      const formData = this.fgAdvancedpipe.getRawValue();

      // Convertir todos los valores a USC antes de enviarlos (el servicio siempre espera USC)
      const convertToUSC = (value: number, field: string) => {
        if (this.unitSystem === UnitSystem.USC) {
          return value; // Ya está en USC
        }

        // Usar getOrConvertValue para aprovechar la cache y mantener precisión exacta
        const convertedValue = this.getOrConvertValue(
          field,
          value,
          UnitSystem.USC,
          (val, unitSys) => {
            // Convertir de SI a USC usando UnitConversionService
            switch (field) {
              case "outerDiameter":
              case "wallThickness":
              case "maxFlawSize":
                return this.unitService.convert(
                  parseFloat(val),
                  UNITS.LENGTH_MM,
                  UNITS.LENGTH_IN,
                );
              case "smys":
              case "uts":
              case "youngModulus":
              case "internalPressure":
              case "externalPressure":
                return this.unitService.convert(
                  parseFloat(val),
                  UNITS.PRESSURE_MPA,
                  UNITS.PRESSURE_KSI,
                );
              case "realAxialLoad":
                return this.unitService.convert(
                  parseFloat(val),
                  UNITS.FORCE_KN,
                  UNITS.FORCE_KIPS,
                );
              default:
                return parseFloat(val); // Sin conversión (porcentajes, dimensionless, etc.)
            }
          },
        );

        return parseFloat(convertedValue);
      };

      // Construir el payload para el endpoint calculate (siempre en USC)
      const calculateRequest = {
        unitSystem: "USC", // Siempre enviar USC
        outerDiameter: convertToUSC(
          parseFloat(formData.outerDiameter) || 0,
          "outerDiameter",
        ),
        wallThickness: convertToUSC(
          parseFloat(formData.wallThickness) || 0,
          "wallThickness",
        ),
        minWallThickness: parseFloat(formData.minWallThickness) || 0, // Porcentaje, no cambia
        grade: formData.grade || "",
        maxFlawSize: convertToUSC(
          parseFloat(formData.maxFlawSize) || 0,
          "maxFlawSize",
        ),
        materialToughness: parseFloat(formData.materialToughness) || 0, // Dimensionless
        ovality: parseFloat(formData.ovality) || 0, // Porcentaje
        eccentricity: parseFloat(formData.eccentricity) || 0, // Porcentaje
        poisson: parseFloat(formData.poisson) || 0, // Dimensionless
        residualStress: parseFloat(formData.residualStress) || 0, // Dimensionless
        smys: convertToUSC(parseFloat(formData.smys) || 0, "smys"),
        uts: convertToUSC(parseFloat(formData.uts) || 0, "uts"),
        youngModulus: convertToUSC(
          parseFloat(formData.youngModulus) || 0,
          "youngModulus",
        ),
        hotRotaryStraightening: formData.hotRotaryStraightening || "",
        realAxialLoad: convertToUSC(
          parseFloat(formData.realAxialLoad) || 0,
          "realAxialLoad",
        ),
        internalPressure: convertToUSC(
          parseFloat(formData.internalPressure) || 0,
          "internalPressure",
        ),
        externalPressure: convertToUSC(
          parseFloat(formData.externalPressure) || 0,
          "externalPressure",
        ),
      };

      this.advancedpipeService.calculate(calculateRequest).subscribe({
        next: (response) => {
          if (response.status?.success) {
            this.advancedpipeResults = response.data;
            this.processResults(this.advancedpipeResults);
            setTimeout(() => {
              this.scrollToResults();
            }, 100);
          } else {
            Swal.fire({
              icon: "warning",
              title: "Calculation completed with warnings",
              text:
                response.status?.message ||
                "The calculation completed but may have warnings.",
            });
            this.advancedpipeResults = response.data;
            this.processResults(this.advancedpipeResults);
          }
        },
        error: (error) => {
          Swal.fire({
            icon: "error",
            title: "Calculation Error",
            text: "An error occurred during the calculation. Please try again.",
          });
        },
      });
    }
  }

  /**
   * Realiza scroll animado hacia la sección de resultados usando Angular puro
   */
  private scrollToResults(): void {
    const element = document.getElementById("simulation-output-title");
    if (!element) {
      // Fallback al contenedor principal si no encuentra el título
      const fallbackElement = document.getElementById(
        "simulation-output-section",
      );
      if (fallbackElement) {
        fallbackElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    // Obtener la posición del elemento de forma más precisa
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const targetPosition = absoluteElementTop - 40; // Reducido para que no se corte el título
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;

    // Si ya estamos cerca o el elemento está arriba, usar scrollIntoView
    if (Math.abs(distance) < 50 || distance < 0) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const duration = 600; // ms
    let start: number;

    const animation = (currentTime: number) => {
      if (start === undefined) {
        start = currentTime;
      }
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function (ease-out)
      const ease = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }

  /**
   * Realiza scroll animado hacia el final de la sección de cálculos detallados usando la misma lógica que scrollToResults
   */
  private scrollToDetailedCalculations(): void {
    const element = document.getElementById("detailed-calculations-section");
    if (!element) {
      return;
    }

    // Obtener la posición del final del elemento
    const elementRect = element.getBoundingClientRect();
    const absoluteElementBottom = elementRect.bottom + window.pageYOffset;
    const targetPosition = absoluteElementBottom - window.innerHeight + 40; // Margen de 40px desde abajo
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;

    // Si ya estamos cerca o no necesita scroll, no hacer nada
    if (Math.abs(distance) < 50) {
      return;
    }

    const duration = 600; // ms - misma duración que scrollToResults
    let start: number;

    const animation = (currentTime: number) => {
      if (start === undefined) {
        start = currentTime;
      }
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function (ease-out) - misma que scrollToResults
      const ease = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }

  // Store converted results for display
  convertedResults: any = null;
  /**
   * Converts backend results to the selected unit system and stores in convertedResults
   */
  private processResults(rawResults: any) {
    this.convertedResults = {
      hoopStressOD: this.convertResultValue(
        rawResults?.hoopStressOD,
        "pressure",
      ),
      vmeStressID: this.convertResultValue(rawResults?.vmeStressID, "pressure"),
      radialStressID: this.convertResultValue(
        rawResults?.radialStressID,
        "pressure",
      ),
      hoopStressID: this.convertResultValue(
        rawResults?.hoopStressID,
        "pressure",
      ),
      burstKleverStuart: this.convertResultValue(
        rawResults?.burstKleverStuart / 1000,
        "pressure",
      ),
      burstHill: this.convertResultValue(rawResults?.burstHill, "pressure"),
      burstCappedEnd: this.convertResultValue(
        rawResults?.burstCappedEnd,
        "pressure",
      ),
      collapseKGT: this.convertResultValue(
        rawResults?.collapseKGT / 1000,
        "pressure",
      ),
      collapseModeAPI: rawResults?.collapseModeAPI ?? null,
      hasErrorCollapseModeAPI: rawResults?.collapseModeAPI
        ? rawResults?.collapseModeAPI.toLowerCase().includes("error")
        : false,
      collapseAPI: this.convertResultValue(
        rawResults?.collapseAPI / 1000,
        "pressure",
      ),
      burstAPI: this.convertResultValue(
        rawResults?.burstAPI / 1000,
        "pressure",
      ),
      pBtensionAPI: this.convertResultValue(
        rawResults?.pBtensionAPI,
        "pressure",
      ),
      effectiveTension: this.convertResultValue(
        rawResults?.effectiveTension,
        "force",
      ),
      dsuTcritTransitionColl: this.convertResultValue(
        rawResults?.dsuTcritTransitionColl,
        "dimensionless",
      ),
      dsuTcritPlasticColl: this.convertResultValue(
        rawResults?.dsuTcritPlasticColl,
        "dimensionless",
      ),
      dsuTcritYieldColl: this.convertResultValue(
        rawResults?.dsuTcritYieldColl,
        "dimensionless",
      ),
      apIcollConstG: this.convertResultValue(
        rawResults?.apIcollConstG,
        "dimensionless",
      ),
      apIcollConstF: this.convertResultValue(
        rawResults?.apIcollConstF,
        "dimensionless",
      ),
      apIcollConstC: this.convertResultValue(
        rawResults?.apIcollConstC,
        "dimensionless",
      ),
      apIcollConstB: this.convertResultValue(
        rawResults?.apIcollConstB,
        "dimensionless",
      ),
      apIcollConstA: this.convertResultValue(
        rawResults?.apIcollConstA,
        "dimensionless",
      ),
      adjusedYieldStrength: this.convertResultValue(
        rawResults?.adjusedYieldStrength,
        "pressure",
      ),
      axialStress: this.convertResultValue(rawResults?.axialStress, "pressure"),
      equivalentCollapsePressure: this.convertResultValue(
        rawResults?.equivalentCollapsePressure,
        "pressure",
      ),
      deltaP: this.convertResultValue(rawResults?.deltaP, "pressure"),
      dsuT: this.convertResultValue(rawResults?.dsuT, "dimensionless"),
      moi: this.convertResultValue(rawResults?.moi, "dimensionless"),
      crossSectionArea: this.convertResultValue(
        rawResults?.crossSectionArea,
        "dimensionless",
      ),
      pipeID: this.convertResultValue(rawResults?.pipeID, "dimensionless"),
      radialStressOD: this.convertResultValue(
        rawResults?.radialStressOD,
        "pressure",
      ),
      vmeStressOD: this.convertResultValue(rawResults?.vmeStressOD, "pressure"),
    };
  }
}
